import clone from 'clone';
import { QRCodeSVG } from 'qrcode.react';
import * as React from 'react';
import { ApplicationSettings, basicValidation, fieldValidation, rng } from '../../amorg-common';
import { clientApi } from '../../api/clientApi';
import { Card } from '../../designSystem/Card';
import { ChipPanel, InfoChip } from '../../designSystem/Chip';
import { Divider } from '../../designSystem/Divider';
import { Label } from '../../designSystem/Label';
import { LoadingPageOverlay } from '../../designSystem/LoadingPageOverlay';
import { MessageCard } from '../../designSystem/MessageCard';
import { Modal } from '../../designSystem/Modal';
import { PageHeader } from '../../designSystem/PageHeader';
import { Button } from '../../designSystem/button/Button';
import { AnimatedCollapsibleContent } from '../../designSystem/collapsible/AnimatedCollapsibleContent';
import { Flex } from '../../designSystem/compositeComponents/FlexHelpers';
import { LinkWithCopyButton } from '../../designSystem/compositeComponents/LinkWithCopyButton';
import { FormField } from '../../designSystem/formField/FormField';
import { OnOffField } from '../../designSystem/formField/OnOffField';
import { TextField } from '../../designSystem/formField/text/TextField';
import { TextInput } from '../../designSystem/inputs/TextInput';
import { SingleColumnLayout } from '../_layouts/SingleColumnLayout';
import { P, Span16 } from '../../designSystem/typography/Body';
import { H2 } from '../../designSystem/typography/Heading';
import { appUrlBuilder } from '../../utils/appUrlBuilder';
import { ProtectedPageContext } from '../_contexts/ProtectedPageContext';
import { MemberPageWrapper } from '../_pageWrappers/MemberPageWrapper';

export function SettingsPage (): JSX.Element
{
    return (
        <MemberPageWrapper selectedMenuItemKey='settings' breadCrumbs={[{ text: 'Indstillinger', url: '' }]}>
            <SettingsPageContent />
        </MemberPageWrapper>
    );
}

function SettingsPageContent (): JSX.Element
{
    const [loading, setLoading] = React.useState(false);
    const [settings, setSettings] = React.useState<ApplicationSettings | null>(null);

    const ctx = React.useContext(ProtectedPageContext);

    React.useEffect(() =>
    {
        async function getData ()
        {
            setLoading(true);
            const settings = await clientApi.applicationSettings.getApplicationSettings();
            setSettings(settings);
            setLoading(false);
        }

        getData();
    }, []);

    async function handleUpdateSettings (newSettings: ApplicationSettings)
    {
        setLoading(true);
        if ((await clientApi.applicationSettings.updateApplicationSettings(newSettings)).isSuccess)
        {
            setSettings(newSettings);
        }
        setLoading(false);
    }


    return (
        <>

            <LoadingPageOverlay loading={loading} />

            <PageHeader narrow headerText="Indstillinger" headerIcon="navigation_settings" />
            <SingleColumnLayout narrow>
                <>
                    {settings &&
                        <>
                            <Card variant="outlined">
                                <Flex flexDirection="column" gap={32}>

                                    <Flex justifyContent='space-between'>
                                        <OnOffField active={settings?.employeePortalSettings.enabled}
                                            onChange={(newValue) =>
                                            {
                                                handleUpdateSettings({
                                                    ...settings,
                                                    employeePortalSettings: {
                                                        ...settings?.employeePortalSettings, enabled: newValue
                                                    }
                                                });
                                            }}
                                            label="Medarbejderportal"
                                            text={settings?.employeePortalSettings.enabled ? 'Medarbejderportalen er åben' : 'Medarbejderportalen er lukket'}
                                            toolTip={<span>Medarbejderportalen er en del af AMORG for virksomhedens medarbejdere. Her kan arbejdsmiljøorganisationen dele information og medarbejderne kan foretage indberetninger.
                                                Hvis portalen er slået fra, vil medarbejderne ikke have adgang til portalen og alt indhold heri vil være skjult.</span>}
                                        />

                                        <FormField
                                            label=""
                                            inputComponent={
                                                <Button variant="outlined"
                                                    text="Nulstil alle aktive sessioner"
                                                    toolTipText='Når du nulstiller alle sessioner, vil alle brugere, der i øjeblikket er logget ind på portalen, blive logget ud og skal logge ind igen for at få adgang. Dette kan være nyttigt, hvis du har foretaget ændringer i indstillingerne for portalen og ønsker at sikre, at alle brugere får de opdaterede indstillinger ved deres næste login.'
                                                    onClick={() => handleUpdateSettings({
                                                        ...settings,
                                                        employeePortalSettings: {
                                                            ...settings?.employeePortalSettings,
                                                            resetTimeStamp: new Date(),
                                                        }
                                                    })} />
                                            }
                                        />

                                    </Flex>

                                    {settings.employeePortalSettings.enabled && (!settings.employeePortalSettings.authentication.magicLink.enabled && !settings.employeePortalSettings.authentication.publicKey.enabled) &&
                                        <MessageCard variant='warning'>
                                            <div>Bemærk: Portalen kan ikke anvendes uden mindst en metode til at logge ind er aktiveret</div>
                                        </MessageCard>
                                    }



                                </Flex>
                            </Card>

                            <Flex flexDirection="column" gap={8}>
                                <H2>Login metoder</H2>
                                <Card variant="outlined">
                                    <Flex flexDirection="column" gap={16}>
                                        <MagicLinkSettings siteId={ctx.user?.siteId || ''} appSettings={settings} onUpdateSettings={handleUpdateSettings} />

                                        <Divider variant="light" />

                                        <PublicLinkSettings siteId={ctx.user?.siteId || ''} appSettings={settings} onUpdateSettings={handleUpdateSettings} />
                                    </Flex>
                                </Card>
                            </Flex>
                        </>
                    }
                </>
            </SingleColumnLayout>

        </>
    );
}

type MagicLinkSettingsProps = {
    siteId: string,
    appSettings: ApplicationSettings,
    onUpdateSettings?: (newSettings: ApplicationSettings) => void;
};
function MagicLinkSettings (props: MagicLinkSettingsProps): JSX.Element
{
    return (
        <Flex flexDirection='column' gap={16}>

            <Flex alignItems="flex-start" gap={16}>
                <Flex flexDirection="column" gap={16} style={{flex:'1 1 0'}}>
                    <OnOffField
                        disabled={!props.appSettings.employeePortalSettings.enabled}
                        active={props.appSettings.employeePortalSettings.authentication.magicLink.enabled}
                        onChange={(newValue) =>
                        {
                            const newSettings = clone(props.appSettings);
                            newSettings.employeePortalSettings.authentication.magicLink.enabled = newValue;
                            props.onUpdateSettings?.(newSettings);
                        }}
                        label=""
                        text="Log ind med virksomheds-email"
                    />

                    <AnimatedCollapsibleContent open={props.appSettings.employeePortalSettings.authentication.magicLink.enabled && props.appSettings.employeePortalSettings.enabled}>
                        <Flex flexDirection="column" gap={16} alignItems="flex-start">
                            {props.appSettings.employeePortalSettings.authentication.magicLink.validDomains.length === 0 &&
                                <MessageCard variant='warning'>
                                    <div>Bemærk: For at kunne logge ind med virksomheds-email, skal der være oprettet mindst ét gyldigt email domæne.</div>
                                </MessageCard>
                            }

                            <Divider />

                            <EmailDomainsPanel
                                disabled={!props.appSettings.employeePortalSettings.enabled || !props.appSettings.employeePortalSettings.authentication.magicLink.enabled}
                                domains={props.appSettings.employeePortalSettings.authentication.magicLink.validDomains}
                                onUpdateDomains={(newDomains) =>
                                {
                                    const newSettings = clone(props.appSettings);
                                    newSettings.employeePortalSettings.authentication.magicLink.validDomains = newDomains;
                                    props.onUpdateSettings?.(newSettings);
                                }}
                            />

                            <Divider />

                            <FormField
                                label="Link til login"
                                toolTip={<span>Dette er det link, som medarbejderne skal bruge for at logge ind på portalen.</span>}
                                inputComponent={
                                    <LinkWithCopyButton
                                        url={appUrlBuilder.portal.launch(props.siteId)}
                                        disabled={!props.appSettings.employeePortalSettings.enabled} />
                                }
                            />
                        </Flex>
                    </AnimatedCollapsibleContent>

                </Flex>

                <Card variant='lightGray' style={{ width: '50%', flex:'1 1 0' }}>
                    <Span16>Når denne funktion er aktiveret, kan medarbejdere logge ind på portalen ved at indtaste deres virksomheds-emailadresse. De vil derefter modtage en e-mail med et unikt link, som de kan klikke på for at få adgang til portalen. Det gør det nemt for medarbejderne at få hurtigt adgang til portalen uden at skulle huske en adgangskode.</Span16>
                </Card>
            </Flex>



        </Flex>
    );
}


type EmailDomainsPanelProps = {
    domains: string[],
    disabled?: boolean,
    onUpdateDomains?: (newDomains: string[]) => void;
};
function EmailDomainsPanel (props: EmailDomainsPanelProps): JSX.Element
{
    const [editing, setEditing] = React.useState(false);

    return (

        <Flex flexDirection="column" gap={8}>
            <Flex alignItems="center" gap={4}>
                <Label>Email domæner med adgang til medarbejderportalen</Label>
                <Button variant="text" size="32px" icon="interface_actions_edit" disabled={props.disabled} onClick={() => setEditing(true)} />
            </Flex>

            <ChipPanel style={{ flexWrap:'wrap'}}>
                {props.domains.map((email, index) =>
                    <InfoChip key={index} variant="filled" >
                        {email}
                    </InfoChip>
                )}
            </ChipPanel>

            <Modal showModal={editing} onCancel={() => setEditing(false)} title="Rediger email domæner"
                content={
                    <EmailDomainsEditor
                        domains={props.domains}
                        onSaveClick={(newDomains) =>
                        {
                            props.onUpdateDomains?.(newDomains);
                            setEditing(false);
                        }} />
                }
            />

        </Flex>
    );
}

type EmailDomainsEditorProps = {
    domains: string[],
    onSaveClick: (newDomains: string[]) => void,
};
function EmailDomainsEditor (props: EmailDomainsEditorProps): JSX.Element
{
    const [emailDomainsText, setEmailDomainsText] = React.useState(props.domains.join(' '));

    type emailDomainsState = 'valid' | 'invalid' | 'duplicate';

    const emailDomainStrings = emailDomainsText.split(',').join(' ').split(';').join(' ').split(' ');
    const emailDomains: { email: string, status: emailDomainsState; }[] = [];
    for (const str of emailDomainStrings)
    {
        if (str.trim() !== '')
        {
            const d = str.trim();
            if (!basicValidation.isValidEmailDomain(d))
            {
                emailDomains.push({ email: d, status: 'invalid' });
            }
            else if (emailDomains.find(e => e.email.toLocaleLowerCase() === str.toLocaleLowerCase()))
                emailDomains.push({ email: d, status: 'duplicate' });
            else
                emailDomains.push({ email: d, status: 'valid' });
        }
    }

    let overallStatus: emailDomainsState = 'valid';
    if (emailDomains.some(e => e.status === 'invalid'))
        overallStatus = 'invalid';
    else if (emailDomains.some(e => e.status === 'duplicate'))
        overallStatus = 'duplicate';

    return (

        <Flex flexDirection="column" gap={8}>
            <Label>Email domæner</Label>
            <TextInput
                placeholder="Email domæner med adgang til portalen"
                value={emailDomainsText}
                onChange={(val) => setEmailDomainsText(val)}
            />
            <ChipPanel>
                {emailDomains.map((email, index) =>
                    <InfoChip key={index} variant={email.status === 'valid' ? 'filled' : 'error'}>
                        {email.email}
                    </InfoChip>
                )}
            </ChipPanel>

            <div style={{ minHeight: '12px' }}>
                {overallStatus === 'invalid' &&
                    <P>Der er et eller flere ugyldige e-mail domæner.</P>
                }
                {overallStatus === 'duplicate' &&
                    <P>Et eller flere e-mail domæner optræder flere gange.</P>
                }
            </div>

            <Button variant="filled"
                disabled={overallStatus !== 'valid'}
                onClick={() => props.onSaveClick(emailDomains.filter(e => e.status === 'valid').map(e => e.email))}
                text='Gem' />
        </Flex>
    );
}

type PublicLinkSettingsProps = {
    siteId: string,
    appSettings: ApplicationSettings,
    onUpdateSettings: (newSettings: ApplicationSettings) => void;
};
function PublicLinkSettings (props: PublicLinkSettingsProps): JSX.Element
{

    const link = appUrlBuilder.portal.loginAccessKey(props.siteId, props.appSettings.employeePortalSettings.authentication.publicKey.accessKey);

    return (

        <Flex style={{flex:'1 1 0'}} gap={16} alignItems="flex-start">
            
            <Flex flexDirection='column' justifyContent='flex-start' gap={16} style={{flex:'1 1 0'}}>

                <Flex>
                    <OnOffField
                        disabled={!props.appSettings.employeePortalSettings.enabled}
                        active={props.appSettings.employeePortalSettings.authentication.publicKey.enabled}
                        onChange={(newValue) =>
                        {
                            const newSettings = clone(props.appSettings);
                            newSettings.employeePortalSettings.authentication.publicKey.enabled = newValue;
                            if (newSettings.employeePortalSettings.authentication.publicKey.accessKey === '')
                                newSettings.employeePortalSettings.authentication.publicKey.accessKey = rng.getRandomCharacters(32, ['lower', 'numeric']);

                            props.onUpdateSettings?.(newSettings);
                        }}
                        label=""
                        text="Log ind med QR kode / link"
                    />
                </Flex>

                <Divider />

                <AnimatedCollapsibleContent open={props.appSettings.employeePortalSettings.authentication.publicKey.enabled && props.appSettings.employeePortalSettings.enabled}>
                    
                    <Flex flexDirection="column" gap={16} alignItems="flex-start">

                        <Card variant="lightGray" style={{ height: 'min-content' }}>
                            <AmorgQRCode url={link} />
                        </Card>


                        <LinkWithCopyButton
                            url={link}
                            disabled={!props.appSettings.employeePortalSettings.enabled} />

                        <PublicLinkModalButton settings={props.appSettings} onUpdateSettings={props.onUpdateSettings} />

                    </Flex>
                </AnimatedCollapsibleContent>

            </Flex>

            <Card variant="lightGray" style= {{flex:'1 1 0'}}>
                <span>Når denne funktion er aktiveret, kan medarbejdere logge ind på portalen ved at benytte det tilhørende link eller QR kode. Bemærk at alle med linket / QR koden får adgang til portalen.</span>
            </Card>

        </Flex>
    );
}

type PublicLinkModalButtonProps = {
    settings: ApplicationSettings,
    onUpdateSettings: (settings: ApplicationSettings) => void;
};
function PublicLinkModalButton (props: PublicLinkModalButtonProps): JSX.Element
{
    const [showModal, setShowModal] = React.useState(false);
    const [newKey, setNewKey] = React.useState(rng.getRandomCharacters(32, ['lower', 'numeric']));

    React.useEffect(() =>
    {
        setNewKey(rng.getRandomCharacters(32, ['lower', 'numeric']));
    }, [showModal]);


    const invalidKey = fieldValidation.applicationSettings.applicationSettingsEmployeePortalSettingsPublicKeyAccessKey({
        ...props.settings,
        employeePortalSettings: {
            ...props.settings.employeePortalSettings,
            authentication: {
                ...props.settings.employeePortalSettings.authentication,
                publicKey: {
                    ...props.settings.employeePortalSettings.authentication.publicKey,
                    accessKey: newKey
                }
            }
        }
    }) === false;

    return (
        <>
            <Button variant="outlined" text="Nulstil link og QR kode"
                toolTipText='Når du nulstiller linket til QR koden, vil det nuværende link og den tilhørende QR kode holde op med at virke.'
                onClick={() => setShowModal(true)}
            />
            <Modal showModal={showModal} onCancel={() => setShowModal(false)} title="Nulstil link og QR kode"
                content={
                    <Flex flexDirection="column" gap={16}>
                        <div>
                            <div>Er du sikker på, at du vil nulstille linket og QR koden?</div>
                            <div>Det nuværende link og den tilhørende QR kode vil holde op med at virke.</div>
                        </div>

                        <TextField
                            label="Nuværende adgangsnøgle"
                            value={props.settings.employeePortalSettings.authentication.publicKey.accessKey}
                            disabled
                        />

                        <FormField
                            label="Ny adgangsnøgle"
                            inputComponent={
                                <Flex justifyContent="space-between" alignItems="center" gap={8}>
                                    <div style={{ flexGrow: 1 }}>
                                        <TextField
                                            value={newKey}
                                            onChange={(val) => setNewKey(val)}
                                        />
                                    </div>
                                    <Button variant="text" icon='navigation_settings'
                                        toolTipText='Generer ny adgangsnøgle'
                                        onClick={() => setNewKey(rng.getRandomCharacters(32, ['lower', 'numeric']))} />
                                </Flex>
                            }
                            inErrorState={invalidKey}
                            message={invalidKey ? 'Adgangsnøglen skal være 32 tegn lang og bestå af små bogstaver og tal.' : undefined}
                        />

                        <Button variant="filled" text="Nulstil" disabled={invalidKey} onClick={() =>
                        {
                            const newSettings = clone(props.settings);
                            newSettings.employeePortalSettings.authentication.publicKey.accessKey = newKey;
                            props.onUpdateSettings(newSettings);
                            setShowModal(false);
                        }} />
                    </Flex>
                }
            />
        </>
    );
}

type AmorgQRCodeProps = {
    url: string;
};
function AmorgQRCode (props: AmorgQRCodeProps): JSX.Element
{
    const containerRef = React.useRef<HTMLDivElement>(null);

    function handleDownload ()
    {
        const svgEl = containerRef.current?.querySelector('svg');
        if (!svgEl) return;

        const serializer = new XMLSerializer();
        const svgStr = serializer.serializeToString(svgEl);
        const blob = new Blob([svgStr], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'qr-kode-medarbejder-portal.svg';
        link.click();
        URL.revokeObjectURL(url);
    }

    return (
        <Flex flexDirection="column" gap={8} alignItems="center">
            <div ref={containerRef}>
                <QRCodeSVG value={props.url} />
            </div>
            <Button variant="outlined" icon="interface_actions_download" text="Gem fil" onClick={handleDownload} />
        </Flex>
    );
}